import { Avatar, Card, CardActionArea, CardContent, Chip, Stack, Typography } from "@mui/material";

import { PhoneWithCopyIcon } from "~ui/molecules/PhoneWithCopyIcon/PhoneWithCopyIcon";
import { makeProfileAvatarUrl } from "~utils/url";

import {
  formatLocalDateTime,
  participantCardBackgroundSx,
  participantDisplayName,
  participantInitials,
  participantRosterCardCenteredRowSx,
  participantRosterCardContainSx,
  participantRosterCardContentSx,
  participantRosterCardDetailRowSx,
  participantRosterCardStackSx,
  participantRosterCardTextColumnSx,
  type ParticipantRow,
  statusLabelForKey,
} from "../../pages/VisitorManagement/emergencyCommandCenterShared";

export interface ParticipantRosterCardProps {
  participant: ParticipantRow;
  onSelect: (participant: ParticipantRow) => void;
  /** When set and equal to `participant.userId`, shows a "You" chip next to the name. */
  currentUserId?: string | null;
}

export function ParticipantRosterCard({ participant: p, onSelect, currentUserId }: ParticipantRosterCardProps) {
  const roles = (p.bubbleRoles ?? []).filter(Boolean);
  const showYouChip = Boolean(currentUserId && p.userId && currentUserId === p.userId);

  return (
    <Card
      sx={{
        ...participantCardBackgroundSx(p.statusKey),
        ...participantRosterCardContainSx,
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
      variant="outlined"
    >
      <CardActionArea
        aria-label={`Update status for ${participantDisplayName(p)}`}
        component="div"
        onClick={() => {
          onSelect(p);
        }}
        sx={{
          alignItems: "stretch",
          borderRadius: 1,
          boxSizing: "border-box",
          display: "flex",
          flex: 1,
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
          minWidth: 0,
          overflow: "hidden",
          width: "100%",
        }}
      >
        <CardContent sx={participantRosterCardContentSx}>
          <Stack alignItems="center" direction="column" gap={1.5} sx={participantRosterCardStackSx}>
            <Avatar
              alt=""
              src={p.userId ? makeProfileAvatarUrl(p.userId) : undefined}
              sx={{ flexShrink: 0, height: 96, width: 96 }}
            >
              {participantInitials(p)}
            </Avatar>
            <Stack spacing={1} sx={participantRosterCardTextColumnSx}>
              {showYouChip ? (
                <Stack alignItems="center" direction="row" flexWrap="wrap" justifyContent="center" sx={{ gap: 1 }}>
                  <Typography component="span" sx={participantRosterCardCenteredRowSx} variant="subtitle1">
                    {participantDisplayName(p)}
                  </Typography>
                  <Chip label="You" size="small" sx={{ flexShrink: 0 }} />
                </Stack>
              ) : (
                <Typography sx={participantRosterCardCenteredRowSx} variant="subtitle1">
                  {participantDisplayName(p)}
                </Typography>
              )}
              {p.phoneNumber?.trim() ? (
                <PhoneWithCopyIcon centered phoneNumber={p.phoneNumber.trim()} showPhoneIcon={false} />
              ) : (
                <Typography color="text.secondary" sx={participantRosterCardCenteredRowSx} variant="body2">
                  —
                </Typography>
              )}
              <Typography color="text.secondary" sx={participantRosterCardDetailRowSx} variant="body2">
                <strong>Role:</strong> {roles.length > 0 ? roles.join(", ") : "—"}
              </Typography>
              <Typography sx={participantRosterCardDetailRowSx} variant="body2">
                <strong>Status:</strong> {statusLabelForKey(p.statusKey)}
              </Typography>
              <Typography color="text.secondary" sx={participantRosterCardDetailRowSx} variant="body2">
                <strong>Last update:</strong> {formatLocalDateTime(p.lastReportedAtUtc)}
              </Typography>
              {p.mapLocationLabel ? (
                <Typography sx={participantRosterCardDetailRowSx} variant="body2">
                  <strong>Location:</strong> {p.mapLocationLabel}
                </Typography>
              ) : null}
            </Stack>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
